/*
When a User clicks on the link to a specific partner while on the Partners explore page
Then they will be taken to the Partner page given the partner's ID.
When they view the Partner page
They will see certain data on that specific partner given the ID (as specified by the design).
*/

//Packages
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
//Components
import InfoRow from "./InfoRow";
import FeaturedProject from "./FeaturedProject";
import FeaturedProjectMobile from "./FeaturedProjectMobile";
//Material UI Components
import {
  Grid,
  Typography,
  Button,
  Stack,
  Avatar,
  Box,
  CircularProgress,
} from "@mui/material";
//Animation
import { motion } from "framer-motion";
//API Route
import { Route } from "../../utils/config";

export default function Partner() {
  const { id } = useParams();

  //State to hold partner data on load
  const [partner, setPartner] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  //Two states for checking projects. One for the initial array that is received by the API,
  //and another that represents the same array filtered to only hold active projects
  //Which will then be displayed to the user.
  const [state, setState] = useState({
    activeProjects: [],
    projects: [],
  });

  //Project types based on partner's project data. Will be filtered to only have one of each type.
  var projectTypes = [];

  //Load partner data and scroll to top of window on component mount
  useEffect(() => {
    getPartner(id);
    window.scrollTo(0, 0);
  }, [id]);

  // Get detailed data on specific partner
  const getPartner = (id) => {
    axios
      .get(`${Route}/Partners/GetPartnerDisplayDetails`, {
        params: {
          partnerId: id,
        },
      })
      .then((res) => {
        setState({
          projects: res.data.projects,
          activeProjects: res.data.projects.filter(
            (project) => !project.isActive
          ),
        });
        setIsLoading(false);
        setPartner(res.data.partner);
      })
      .catch((err) => {
        throw err;
      });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <Grid container>
        <Grid container item xs={12} md={8} justifyContent="center">
          <Grid item mt={2} mb={2} xs={10} md={8}>
            <Typography variant="body2" color="#8898AA">
              HOME <span styles="color: black">/</span> PARTNERS
              <span styles="color: black">
                {" "}
                /{" "}
                {partner.companyName
                  ? partner.companyName.toUpperCase()
                  : "PARTNER"}
              </span>
            </Typography>
          </Grid>
          <Grid container item alignItems="center" xs={10} md={8} mt={2}>
            <Grid container item alignItems="center">
              <Avatar sx={{ marginRight: 2 }}></Avatar>

              <Typography fontSize="20px" fontWeight="500">
                {partner.companyName}
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={10} md={8} mb={2} mt={2}>
            <Stack spacing={1}>
              <Typography variant="body1">Company Overview:</Typography>
              <Typography fontSize="14px" color="#58585A">
                {partner.description ? partner.description : "No overview"}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={10} md={8} mb={4}>
            <Stack spacing={1}>
              <Typography variant="body1">Project Type:</Typography>
              <Grid container spacing={0.5}>
                {state.projects.map((project) => {
                  if (!projectTypes.includes(project.projectType)) {
                    projectTypes.push(project.projectType);
                    return (
                      <Grid item key={project.id}>
                        <Button variant="contained" color="primary">
                          {project.projectType
                            ? project.projectType
                            : "No data"}
                        </Button>
                      </Grid>
                    );
                  }
                  return "";
                })}
              </Grid>
            </Stack>
          </Grid>
          <Grid
            container
            item
            xs={10}
            md={8}
            mb={{ md: 4 }}
            justifyContent="space-between"
          >
            <InfoRow label="Country" data={partner.country}></InfoRow>
            <InfoRow label="Region" data={partner.region}></InfoRow>
            <InfoRow
              label="# of Employees"
              data={partner.numberOfEmployees}
            ></InfoRow>
            <InfoRow
              label="Expertise"
              data={partner.geographicalExpertise}
            ></InfoRow>
            <InfoRow
              label="Year Established"
              data={partner.yearEstablished}
            ></InfoRow>
            <InfoRow
              label="Active Projects"
              data={
                state.activeProjects ? state.activeProjects.length : undefined
              }
            ></InfoRow>
          </Grid>
        </Grid>
        <Grid container item xs={12} md={4} justifyContent="center">
          <Grid
            container
            item
            justifyContent="flex-start"
            borderTop={{ xs: "#7070702d solid 2px", md: "none" }}
            xs={10}
          >
            <Box mt={{ xs: 2, md: 5 }} mb={{ xs: 4, md: 0 }}>
              {/* These two buttons need functionality. Ask client how they would like to implement them. */}
              <Button variant="contained" color="light" sx={{ marginRight: 2 }}>
                <a
                  href={`mailto:${partner.email}?subject=A question about your Love Big Project`}
                  style={{ textDecoration: "none", color: "white" }}
                >
                  Ask a Question?
                </a>
              </Button>
              <Button variant="contained" color="light">
                Visit Website
              </Button>
            </Box>
          </Grid>
          <Grid container item xs={10} md={12} justifyContent="center">
            <Typography
              variant="body1"
              fontSize={{ md: "20px" }}
              mb={{ xs: 2, md: 1 }}
              color="#052442"
              borderBottom={{ xs: "#7070703d 2px solid", md: "none" }}
              width="100%"
            >
              Featured Projects
            </Typography>
            {!isLoading ? (
              <>
                <Grid
                  container
                  item
                  justifyContent="center"
                  columnSpacing={2}
                  spacing={4}
                  mb={2}
                  display={{ md: "none" }}
                >
                  {state.projects.length > 0 ? (
                    <>
                      {state.projects.slice(0, 4).map((project) => {
                        return (
                          <FeaturedProjectMobile
                            key={project.id}
                            id={project.id}
                            name={project.name}
                            type={project.projectType}
                            overview={project.overview}
                          ></FeaturedProjectMobile>
                        );
                      })}{" "}
                    </>
                  ) : (
                    <Grid item>
                      <h1>No Projects to Display</h1>
                    </Grid>
                  )}
                </Grid>
                <Grid
                  container
                  item
                  justifyContent="center"
                  mb={2}
                  display={{ xs: "none", md: "block" }}
                >
                  {state.projects.length > 0 ? (
                    <>
                      <Stack spacing={2}>
                        {state.projects.slice(0, 4).map((project) => {
                          return (
                            <FeaturedProject
                              key={project.id}
                              id={project.id}
                              name={project.name}
                              type={project.projectType}
                              overview={project.overview}
                            ></FeaturedProject>
                          );
                        })}
                      </Stack>
                    </>
                  ) : (
                    <Grid item>
                      <h1>No Projects to Display</h1>
                    </Grid>
                  )}
                </Grid>
              </>
            ) : (
              <Grid container justifyContent="center" mt={2} mb={2}>
                <Grid item>
                  <CircularProgress></CircularProgress>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </motion.div>
  );
}
